import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  page: number;
  pageCount: number;
  onPage: (page: number) => void;
  /** Accessible name for the nav landmark. */
  label?: string;
  className?: string;
}

const PG = 'inline-flex h-9 min-w-[36px] items-center justify-center rounded-[10px] border px-2 font-mono text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-40';
const PG_IDLE = 'border-line-strong bg-surface text-navy hover:border-line-hover dark:text-ink';
const PG_ON = 'border-navy bg-navy text-white';

/** Page numbers to show; null stands for an ellipsis gap. */
export function pagerWindow(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const keep = new Set([1, total, current - 1, current, current + 1].filter((p) => p >= 1 && p <= total));
  const sorted = Array.from(keep).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 2) out.push(null);
    else if (i > 0 && p - sorted[i - 1] === 2) out.push(p - 1);
    out.push(p);
  });
  return out;
}

export default function Pager({ page, pageCount, onPage, label = 'Pagination', className = '' }: Props) {
  if (pageCount <= 1) return null;
  const current = Math.min(Math.max(1, page), pageCount);
  return (
    <nav aria-label={label} className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      <button type="button" aria-label="Previous page" disabled={current <= 1}
        onClick={() => onPage(current - 1)} className={`${PG} ${PG_IDLE}`}>
        <ChevronLeft size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>
      {pagerWindow(current, pageCount).map((p, i) => p === null ? (
        <span key={`gap-${i}`} aria-hidden="true" className="px-1 font-mono text-muted">…</span>
      ) : (
        <button key={p} type="button" aria-label={`Page ${p}`} aria-current={p === current ? 'page' : undefined}
          onClick={() => onPage(p)} className={`${PG} ${p === current ? PG_ON : PG_IDLE}`}>
          {p}
        </button>
      ))}
      <button type="button" aria-label="Next page" disabled={current >= pageCount}
        onClick={() => onPage(current + 1)} className={`${PG} ${PG_IDLE}`}>
        <ChevronRight size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>
    </nav>
  );
}
